import { ArrowUpRight, Briefcase, FileText, FolderKanban, Github, Linkedin, Youtube } from 'lucide-react';
import { HERO, SITE, SOCIAL } from '../data/site';
import SectionLink from './SectionLink';
import ResumeLink from './ResumeLink';
import Stats from './Stats';
import Emphasis from './Emphasis';
import { useLocalTime } from '../hooks/useLocalTime';
import { trackEvent } from '../utils/analytics';

const ICONS = { GitHub: Github, LinkedIn: Linkedin, YouTube: Youtube };

function StatusCard() {
  const time = useLocalTime(SITE.timezone);
  return (
    <div
      className="card rise w-full max-w-md font-mono text-[12.5px] leading-relaxed overflow-hidden"
      style={{ '--d': '420ms' }}
      aria-label="Current status"
    >
      <div className="flex items-center gap-2 px-4 h-10 border-b border-line">
        <span className="h-2.5 w-2.5 rounded-full bg-[#ff5f57]/80" />
        <span className="h-2.5 w-2.5 rounded-full bg-[#febc2e]/80" />
        <span className="h-2.5 w-2.5 rounded-full bg-[#28c840]/80" />
        <span className="ml-3 text-[11px] text-faint">janmesh@dublin ~ status</span>
      </div>
      <dl className="p-5 space-y-2">
        <div className="flex gap-4">
          <dt className="w-20 shrink-0 text-faint">local</dt>
          <dd className="text-muted">
            {time} <span className="text-faint">IST · Dublin</span>
          </dd>
        </div>
        {HERO.status.map((row) => (
          <div key={row.k} className="flex gap-4">
            <dt className="w-20 shrink-0 text-faint">{row.k}</dt>
            <dd className={row.k === 'open_to' ? 'text-accent' : 'text-ink/90'}>{row.v}</dd>
          </div>
        ))}
        <div className="flex gap-4 pt-2">
          <dt className="w-20 shrink-0 text-faint">available</dt>
          <dd className="text-ink flex items-center gap-2">
            <span className="relative flex h-2 w-2">
              <span className="absolute inset-0 rounded-full bg-accent animate-pulse-dot" />
              <span className="relative h-2 w-2 rounded-full bg-accent" />
            </span>
            yes
            <span className="inline-block w-[7px] h-[14px] bg-accent/80 animate-blink ml-1" aria-hidden="true" />
          </dd>
        </div>
      </dl>
    </div>
  );
}

export default function Hero() {
  return (
    <section
      id="home"
      className="relative px-6 lg:px-10 pt-24 sm:pt-32 lg:pt-40 pb-16 lg:pb-20"
      aria-label="Introduction"
    >
      <div className="max-w-page mx-auto">
        <div className="grid lg:grid-cols-12 gap-10 lg:gap-8 items-end">
          <div className="lg:col-span-8">
            <p className="label rise flex items-center gap-3 mb-8" style={{ '--d': '0ms' }}>
              <span className="h-px w-8 bg-accent" aria-hidden="true" />
              {HERO.eyebrow}
            </p>

            <h1
              className="display rise text-[2.75rem] sm:text-6xl lg:text-[5.5rem] xl:text-[6.25rem] leading-[0.98]"
              style={{ '--d': '80ms' }}
            >
              <span className="sr-only">{SITE.name}. </span>
              <Emphasis text={HERO.headline} />
            </h1>

            <p
              className="rise mt-8 max-w-xl text-base sm:text-lg leading-relaxed text-muted"
              style={{ '--d': '200ms' }}
            >
              {HERO.intro}
            </p>

            <div className="rise mt-9 grid grid-cols-2 sm:flex sm:flex-wrap sm:items-center gap-3" style={{ '--d': '300ms' }}>
              <SectionLink href="#experience" className="btn-primary justify-center">
                <Briefcase size={16} aria-hidden="true" />
                Experience
              </SectionLink>
              <SectionLink href="#projects" className="btn-ghost justify-center">
                <FolderKanban size={16} aria-hidden="true" />
                Projects
              </SectionLink>
              <ResumeLink className="btn-ghost justify-center col-span-2 sm:col-auto">
                <FileText size={16} aria-hidden="true" />
                Resume
                <ArrowUpRight size={14} aria-hidden="true" className="opacity-60" />
              </ResumeLink>
              <div className="col-span-2 sm:col-auto flex items-center gap-1 -ml-2 sm:ml-1 mt-1 sm:mt-0">
                {SOCIAL.filter((s) => ICONS[s.label]).map(({ label, href }) => {
                  const Icon = ICONS[label];
                  return (
                    <a
                      key={label}
                      href={href}
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label={label}
                      className="h-11 w-11 rounded-full flex items-center justify-center text-muted hover:text-ink hover:bg-white/[0.06] transition-colors"
                      onClick={() => trackEvent('contact_click', { method: label, source: 'hero' })}
                    >
                      <Icon size={18} />
                    </a>
                  );
                })}
              </div>
            </div>
          </div>

          <div className="lg:col-span-4 flex lg:justify-end">
            <StatusCard />
          </div>
        </div>

        <Stats />
      </div>
    </section>
  );
}
